/**
 * Resimden barkod okuma.
 *
 * WhatsApp'tan gelen bir fotoğrafta genellikle birden fazla QR bulunur;
 * hepsi tek geçişte çıkarılır. Tarayıcının yerleşik `BarcodeDetector`
 * çözücüsü kullanılır — donanım hızlandırmalı, ek kütüphane gerektirmez.
 */

interface BarcodeDetectorLike {
  detect: (source: ImageBitmapSource) => Promise<
    Array<{ rawValue: string; format: string }>
  >;
}

interface BarcodeDetectorCtor {
  new (options?: { formats?: string[] }): BarcodeDetectorLike;
}

function getCtor(): BarcodeDetectorCtor | undefined {
  return (globalThis as { BarcodeDetector?: BarcodeDetectorCtor }).BarcodeDetector;
}

export function imageScanSupported(): boolean {
  return !!getCtor() && typeof createImageBitmap === 'function';
}

export interface BulunanKod {
  deger: string;
  format: string;
}

/**
 * Küçük veya uzaktan çekilmiş QR'lar ilk geçişte okunamayabilir. Bu yüzden
 * bulunamazsa resim büyütülerek tekrar denenir: WhatsApp fotoğrafları sıkıştırılmış
 * geldiği için bu ikinci deneme pratikte fark yaratıyor.
 */
const BUYUTME_KATSAYILARI = [1, 2, 3];

export async function scanImageForCodes(kaynak: Blob | File): Promise<BulunanKod[]> {
  const ctor = getCtor();
  if (!ctor) throw new Error('Bu cihaz resimden barkod okumayı desteklemiyor');

  const detector = new ctor({
    formats: [
      'qr_code',
      'data_matrix',
      'aztec',
      'pdf417',
      'ean_13',
      'ean_8',
      'code_128',
      'code_39',
      'code_93',
      'itf',
      'upc_a',
      'upc_e',
    ],
  });

  const bitmap = await createImageBitmap(kaynak);

  try {
    for (const kat of BUYUTME_KATSAYILARI) {
      const hedef = kat === 1 ? bitmap : await olcekle(bitmap, kat);

      let sonuc: Array<{ rawValue: string; format: string }>;
      try {
        sonuc = await detector.detect(hedef);
      } catch {
        sonuc = [];
      } finally {
        if (hedef !== bitmap && 'close' in hedef) {
          (hedef as ImageBitmap).close();
        }
      }

      const kodlar = benzersiz(sonuc);
      if (kodlar.length > 0) return kodlar;
    }

    return [];
  } finally {
    bitmap.close();
  }
}

async function olcekle(bitmap: ImageBitmap, kat: number): Promise<ImageBitmap> {
  const g = Math.min(bitmap.width * kat, 4000);
  const y = Math.min(bitmap.height * kat, 4000);

  const tuval = document.createElement('canvas');
  tuval.width = g;
  tuval.height = y;

  const ctx = tuval.getContext('2d');
  if (!ctx) throw new Error('Görüntü işlenemedi');

  // Barkod kenarlarının bulanıklaşmaması için yumuşatma kapatılır.
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(bitmap, 0, 0, g, y);

  return createImageBitmap(tuval);
}

function benzersiz(
  sonuc: Array<{ rawValue: string; format: string }>,
): BulunanKod[] {
  const gorulen = new Set<string>();
  const out: BulunanKod[] = [];

  for (const s of sonuc) {
    const deger = s.rawValue?.trim();
    if (!deger || gorulen.has(deger)) continue;
    gorulen.add(deger);
    out.push({ deger, format: s.format });
  }

  return out;
}
